import React, { useEffect, useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { listReloadYards } from "../../../graphql/queries";

import {
  Backdrop,
  Card,
  CircularProgress,
  makeStyles,
  Tooltip,
  useMediaQuery,
} from "@material-ui/core";
import { orange } from "@material-ui/core/colors";
import { RotateLeft as StripeRefreshIcon } from "@material-ui/icons";

import {
  Button,
  DateField,
  EditButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  NumberField,
  RefreshButton,
  SelectInput,
  TextField,
  TopToolbar,
  useListContext,
  useNotify,
  useRefresh,
} from "react-admin";

import { Pagination } from "../../../components/Pagination";
import { ReloadYardPeak } from "./ReloadYardPeak";
import { SearchField } from "../../../components/common/SearchField";
import { useSearchableList } from "../../../dataProvider/useSearchableList";
import {
  CheckIcon,
  ErrorFilledIcon,
  WarningIcon,
  XIcon,
} from "../../../components/common/iconography";
import {
  ReloadYardStatus,
  ReloadYardStatusChoices,
} from "../../../utils/constants";
import VerifiedStoredIcon from "@material-ui/icons/ErrorOutline";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { logError, retrieveStripeSubscription } from "../../../utils/appUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

const ReloadYardSearchField = ({ label, alwaysOn }) => {
  const listContext = useListContext();
  const { displayedFilters, filterValues, setFilters } = listContext;

  const handleSearch = (value) => {
    if (value) {
      delete filterValues.or;
      setFilters(
        {
          ...filterValues,
          or: [
            { name: { matchPhrasePrefix: value } },
            { name: { wildcard: `*${value}*` } },
            { city: { matchPhrasePrefix: value } },
            { city: { wildcard: `*${value}*` } },
            { state: { matchPhrasePrefix: value } },
            { state: { wildcard: `*${value}*` } },
          ],
        },
        displayedFilters,
      );
    } else {
      delete filterValues.or;
      setFilters(filterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="ReloadYardSearchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const handleStripeRefresh = async (notify, refresh, setLoading) => {
  try {
    setLoading(true);
    let nextToken = null;
    const limit = 250;

    do {
      const result = await API.graphql(
        graphqlOperation(listReloadYards, { limit, nextToken }),
      );
      const reloadYards = result.data.listReloadYards.items;
      nextToken = result.data.listReloadYards.nextToken;

      for (const reloadYard of reloadYards) {
        await retrieveStripeSubscription(reloadYard);
      }
    } while (nextToken);

    notify("All yards updated");
  } catch (error) {
    logError("adminApp:reloadYardList:handleStripeRefresh", error, notify);
  } finally {
    setLoading(false);
    refresh();
  }
};

const getListFilters = () => [
  <ReloadYardSearchField label="Search Reload Yards" alwaysOn />,
  <SelectInput
    source="status.eq"
    label="Subscription"
    variant="outlined"
    resettable
    allowEmpty
    alwaysOn
    choices={ReloadYardStatusChoices}
    helperText={false}
  />,
];

const ListActions = ({ xsDown }) => {
  const classes = useStyles();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);

  return (
    <TopToolbar>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {!xsDown && <FilterButton />}
      <Button
        label="Strip Refresh"
        color="primary"
        onClick={() => handleStripeRefresh(notify, refresh, setLoading)}
      >
        <StripeRefreshIcon />
      </Button>
      <RefreshButton />
    </TopToolbar>
  );
};

const SubscriptionStatusField = (record) => {
  if (!record) return null;

  const status = record.status;
  const subscriptionStatus = record.subscription?.status;
  const name = record.subscription?.name || "";

  return (
    <Tooltip title={name} arrow>
      <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        {status === ReloadYardStatus.ACTIVE && (
          <CheckIcon style={{ color: "green" }} fontSize="medium" />
        )}
        {status === ReloadYardStatus.CANCELED && (
          <XIcon style={{ color: "red" }} fontSize="medium" />
        )}
        {status === ReloadYardStatus.TRIALING && (
          <VerifiedStoredIcon style={{ color: "yellow" }} fontSize="medium" />
        )}
        {status === ReloadYardStatus.SUSPENDED && (
          <WarningIcon style={{ color: orange[600] }} fontSize="medium" />
        )}
        {status === ReloadYardStatus.TERMINATED && (
          <ErrorFilledIcon style={{ color: "red" }} fontSize="medium" />
        )}
        <span>
          {subscriptionStatus} / {status}
        </span>
      </span>
    </Tooltip>
  );
};

export const ReloadYardList = (props) => {
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const [defaultFilter] = useState({ active: true });
  const [defaultSearchFilter] = useState({
    active: { eq: "true" },
  });

  const defaultSort = {
    field: `{"key":"reloadYardByName", "search":"name"}`,
    order: "ASC",
  };

  const sessionPerPage = Number(
    localStorage.getItem(`${props.resource}_perPage`),
  );
  const [defaultPerPage] = useState(sessionPerPage || 10);

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Reload Yards",
    defaultFilter,
    defaultSearchFilter,
    defaultPerPage,
    defaultSort,
    resource: "reloadYards",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // reset list on automatic stale list reset
      // Extract the key query from defaultSort
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;

      // Check for the key's presence in filter
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext?.filter]);

  return (
    <ListContextProvider value={listContext}>
      <ListToolbar
        {...props}
        title="Reload Yards"
        bulkActionButtons={false}
        filters={getListFilters(mdUp)}
        actions={<ListActions xsDown={xsDown} />}
      />
      <Card>
        {xsDown ? (
          <CustomizableDatagrid
            rowClick="show"
            storageProp="ReloadYard_xsmall"
            defaultColumns={["name", "city", "state"]}
            expand={<ReloadYardPeak />}
          >
            <TextField
              source="name"
              sortable={true}
              sortBy={`{"key":"reloadYardByName", "search":"name"}`}
            />
            <TextField source="city" sortable={false} />
            <TextField source="state" sortable={false} />
            <EditButton />
          </CustomizableDatagrid>
        ) : (
          <CustomizableDatagrid
            rowClick="show"
            expand={<ReloadYardPeak />}
            storageProp="ReloadYard_large"
            defaultColumns={[
              "name",
              "state",
              "createdAt",
              "subscription.status",
              // "status",
              "servingCarrier",
            ]}
          >
            <TextField
              source="name"
              sortable={true}
              sortBy={`{"key":"reloadYardByName", "search":"name"}`}
            />
            <TextField source="city" sortable={true} />
            <TextField source="state" label="State" sortable={true} />
            <DateField source="createdAt" sortable={true} />
            <FunctionField
              source="subscription.status"
              label="Subscription"
              render={SubscriptionStatusField}
              sortable={false}
            />
            {/* <TextField source="status" label="Status" sortable={true} /> */}
            <TextField
              source="servingCarrier"
              label="Carrier"
              sortable={true}
            />
            <NumberField
              source="trackCapacity"
              label="Track (Feet)"
              sortable={true}
            />
            <NumberField
              source="lotCapacity"
              label="Lot (Acres)"
              sortable={true}
            />
            <TextField
              source="warehouseCapacity"
              label="Warehouse Capacity"
              sortable={true}
            />
            <EditButton />
          </CustomizableDatagrid>
        )}
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};
